import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { isTouchDevice } from '../../utils/helpers';
import {
  Container,
  InputsContainer,
  PredefinedSelectionButton,
  PredefinedSelectionsColumnContainer,
  PredefinedSelectionsRowContainer,
  RangeSelectionContainer,
  StyledDateRangePicker,
  StyledDateTimeInput,
} from './DateRangePicker.styles';
import { setupDateHighlighting } from './utils';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

interface PredefinedSelection {
  name: string;
  range: DateRange;
}

interface DateRangePickerProps {
  onChange?: (range: DateRange) => void;
  maxDaysCount?: number;
  singleDateSelection?: boolean;
  minDate?: Date;
  selectedRange?: DateRange;
  maximumDate?: Date;
  setIsWrongRange?: (isWrong: boolean) => void;
  highlightedDates?: Array<string | Date>;
  hidePredefinedSelections?: boolean;
}

const DateRangePicker = ({
  onChange,
  maxDaysCount,
  singleDateSelection,
  minDate,
  selectedRange,
  maximumDate,
  setIsWrongRange,
  highlightedDates = [],
  hidePredefinedSelections = false,
  ...rest
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const [valueRange, setValueRange] = useState(selectedRange || moment.range(moment(), moment()));
  const [valueFrom, setValueFrom] = useState<Date | Moment.Moment>(
    selectedRange?.start.clone().startOf('day') || minDate || undefined,
  );
  const [valueTo, setValueTo] = useState<Date | Moment.Moment>(selectedRange?.end?.clone().startOf('day') || undefined);
  const [predefinedSelectiom, setPredefinedSelectiom] = useState<PredefinedSelection>(undefined);
  const [startDateSelected, setStartDateSelected] = useState<Date>(undefined);
  const [pendingDate, setPendingDate] = useState<Date>(undefined);

  const predefinedSelections: PredefinedSelection[] = [
    {
      name: t('components.dateRangePicker.last30days'),
      range: moment.range(moment().subtract(30, 'days'), moment()),
    },
    {
      name: t('components.dateRangePicker.thisMonth'),
      range: moment.range(moment().startOf('month'), moment().endOf('month')),
    },
    {
      name: t('components.dateRangePicker.lastMonth'),
      range: moment.range(
        moment().subtract(1, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month'),
      ),
    },
  ];

  const normalizeRange = (range: DateRange) => {
    if (!maximumDate) return range;

    return range.intersect(moment.range(range.start, maximumDate));
  };

  const onPredefinedSelect = (selection: PredefinedSelection) => {
    handleSelect(normalizeRange(selection.range));
    setPredefinedSelectiom(selection);
    setStartDateSelected(undefined);
  };

  const isWrongRange = (range: DateRange) => {
    if (maxDaysCount) {
      const rangeDaysCount = moment.duration(range.end.diff(range.start)).asDays() + 1;
      return rangeDaysCount > maxDaysCount;
    }
    return false;
  };

  const PredefinedSelections = () => (
    <>
      {predefinedSelections.map((selection: PredefinedSelection, index: number) => (
        <PredefinedSelectionButton
          isFirst={index === 0}
          isSelected={selection.name === predefinedSelectiom?.name}
          onClick={() => onPredefinedSelect(selection)}
        >
          {selection.name}
        </PredefinedSelectionButton>
      ))}
    </>
  );

  const selectRange = (range: DateRange) => {
    if (singleDateSelection) {
      // In single date mode, always allow selection
      setValueRange(range);
      setValueFrom(range.start);
      setValueTo(range.end);
      onChange?.(range);
      setIsWrongRange?.(false);
    } else if (isWrongRange(range)) {
      toast.error(t('components.dateRangePicker.maxDaysError', { count: maxDaysCount }));
    } else {
      setValueRange(range);
      setValueFrom(range.start);
      setValueTo(range.end);
      onChange?.(range);
    }
  };

  const handleSelectStart = (selectStartDate: Date | Moment.Moment) => {
    if (!isTouchDevice() && !singleDateSelection) {
      // For non-touch devices in range mode, don't handle here
      return;
    }
    const date: Date = Moment.isMoment(selectStartDate) ? selectStartDate.toDate() : selectStartDate;
    setPendingDate(date);
    if (singleDateSelection) {
      // For single date selection, immediately select the date
      const singleDateRange = moment.range(date, date);
      selectRange(singleDateRange);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else if (startDateSelected) {
      const range =
        startDateSelected < date ? moment.range(startDateSelected, date) : moment.range(date, startDateSelected);
      selectRange(range);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else {
      setStartDateSelected(date);
      setValueFrom(date);
      setValueRange(moment.range(date, date));
    }
  };

  const handleSelect = (range: DateRange) => {
    if (singleDateSelection) {
      // For single date selection, create a range with the same start and end date
      const singleDateRange = moment.range(range.start, range.start);
      selectRange(singleDateRange);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else {
      selectRange(range);
      setPredefinedSelectiom(undefined);
    }
  };

  const onChangeInputValue = (range: DateRange) => {
    if (range.end >= range.start) {
      setValueRange(range);
    }
    setPredefinedSelectiom(undefined);
    onChange?.(range);
  };

  const onChangeStartValue = (date: Date) => {
    const startMoment = moment(date).startOf('day');
    // Use existing end date if it exists and is after start, otherwise set to same day
    let endMoment = valueRange.end ? moment(valueRange.end).startOf('day') : startMoment.clone();

    // If end is before or same as start, set end to end of start day (single day range)
    if (endMoment.isSameOrBefore(startMoment, 'day')) {
      endMoment = startMoment.clone().endOf('day');
    } else {
      endMoment = endMoment.endOf('day');
    }

    const range = moment.range(startMoment, endMoment);
    setValueFrom(startMoment.toDate());
    onChangeInputValue(normalizeRange(range));
  };

  const onChangeEndValue = (date: Date) => {
    const endMoment = moment(date).startOf('day');
    const startMoment = valueRange.start ? moment(valueRange.start).startOf('day') : endMoment.clone();

    // If end is before start, set start to same day as end (single day range)
    if (endMoment.isBefore(startMoment, 'day')) {
      const range = moment.range(endMoment, endMoment.clone().endOf('day'));
      setValueFrom(endMoment.toDate());
      setValueTo(endMoment.clone().endOf('day').toDate());
      onChangeInputValue(normalizeRange(range));
    } else {
      const range = moment.range(startMoment, endMoment.clone().endOf('day'));
      setValueTo(endMoment.clone().endOf('day').toDate());
      onChangeInputValue(normalizeRange(range));
    }
  };

  React.useEffect(() => {
    // FIX: there was an issue on the mobile devices: touhed date cell is selecting after scroll/swipe action
    const handleDateTouchEnd = (e) => {
      e.stopPropagation();
    };

    window.addEventListener('touchmove', function () {
      const elements = document.getElementsByClassName('DateRangePicker__Date');
      for (let i = 0; i < elements.length; i++) {
        elements[i].addEventListener('touchend', handleDateTouchEnd);
      }
    });

    window.addEventListener('touchend', function () {
      const elements = document.getElementsByClassName('DateRangePicker__Date');
      for (let i = 0; i < elements.length; i++) {
        elements[i].removeEventListener('touchend', handleDateTouchEnd);
      }
    });
  }, []);

  useEffect(() => {
    updateInvalidState(
      moment(valueFrom) > moment(valueTo) ||
        valueFrom > maximumDate ||
        valueTo > maximumDate ||
        (minDate && (valueFrom < minDate || valueTo < minDate)),
    );
  }, [valueFrom, valueTo, maximumDate, minDate]);

  useEffect(() => {
    if (valueRange && valueRange.start && valueRange.end) {
      // Initial application of classes
      const applyClasses = () => {
        document
          .querySelectorAll(
            '.DateRangePicker__Date--range-start, .DateRangePicker__Date--range-end, .DateRangePicker__Date--pending',
          )
          .forEach((el) => {
            el.classList.remove('DateRangePicker__Date--range-start');
            el.classList.remove('DateRangePicker__Date--range-end');
            el.classList.remove('DateRangePicker__Date--pending');
          });

        const selectedDates = document.querySelectorAll('.DateRangePicker__Date--is-selected');

        if (selectedDates.length > 0) {
          selectedDates[0].classList.add('DateRangePicker__Date--range-start');
          selectedDates[selectedDates.length - 1].classList.add('DateRangePicker__Date--range-end');
        }

        // Add class for pending date
        if (pendingDate && !singleDateSelection) {
          const dateElements = document.querySelectorAll('.DateRangePicker__Date');
          dateElements.forEach((element) => {
            const dateLabel = element.querySelector('.DateRangePicker__DateLabel')?.textContent;
            const dateClasses = element.classList;

            // Get the date from the element based on class and text
            const isPendingMonth =
              dateClasses.contains('DateRangePicker__Date--otherMonth') !==
              moment(pendingDate).isSame(moment(), 'month');
            const isCorrectDate = dateLabel && parseInt(dateLabel, 10) === pendingDate.getDate();

            if (isCorrectDate && isPendingMonth) {
              element.classList.add('DateRangePicker__Date--pending');
            }
          });
        }

        // After applying range classes, re-trigger highlighting to ensure
        // highlighted dates that are now selected get their highlight removed
        if (highlightedDates.length > 0) {
          const event = new CustomEvent('rangeClassesApplied');
          document.dispatchEvent(event);
        }
      };

      // Apply classes initially
      setTimeout(applyClasses, 10);

      // Set up event listeners for hover to ensure classes stay applied
      const calendar = document.querySelector('.DateRangePicker__MonthDates');
      if (calendar) {
        const handleMouseEnter = () => {
          setTimeout(() => {
            applyClasses();
          }, 5);
        };

        // Add listeners to each date cell
        const dateCells = document.querySelectorAll('.DateRangePicker__Date');
        dateCells.forEach((cell) => {
          cell.addEventListener('mouseenter', handleMouseEnter);
        });

        // Clean up function
        return () => {
          dateCells.forEach((cell) => {
            cell.removeEventListener('mouseenter', handleMouseEnter);
          });
        };
      }
    }
    return () => null;
  }, [valueRange, pendingDate, singleDateSelection, highlightedDates]);

  // Sync selectedRange prop to internal state
  useEffect(() => {
    if (selectedRange) {
      setValueRange(selectedRange);
      setValueFrom(selectedRange.start.clone().startOf('day'));
      setValueTo(selectedRange.end?.clone().startOf('day') || selectedRange.start.clone().startOf('day'));
    } else if (selectedRange === undefined) {
      const defaultRange = moment.range(moment(), moment());
      setValueRange(defaultRange);
      setValueFrom(minDate || undefined);
      setValueTo(undefined);
    }
  }, [selectedRange, minDate]);

  // Mark days with additions
  useEffect(() => {
    return setupDateHighlighting(highlightedDates);
  }, [highlightedDates, valueRange]);

  const shouldSkipPendingState = () => {
    if (!isTouchDevice() || !pendingDate) return false;
    return !valueRange.contains(pendingDate);
  };

  const updateInvalidState = (isInvalid: boolean) => {
    setIsWrongRange?.(isInvalid);
  };

  const getInputError = (isFrom: boolean) => {
    const inputId = isFrom ? 'inputFrom' : 'inputTo';
    if (document.getElementById(inputId) !== document.activeElement) return undefined;

    if (moment(valueFrom) > moment(valueTo)) {
      return isFrom ? t('components.input.invalidFromDate') : t('components.input.invalidToDate');
    }

    if (minDate) {
      const value = isFrom ? valueFrom : valueTo;
      if (value < minDate) {
        return t('components.input.minimumDateError');
      }
    }

    if (!maximumDate) return undefined;

    const value = isFrom ? valueFrom : valueTo;
    if (value > maximumDate) {
      return t('components.input.maximumDateError');
    }

    return undefined;
  };

  return (
    <Container {...rest}>
      <RangeSelectionContainer>
        <InputsContainer>
          {singleDateSelection ? (
            <StyledDateTimeInput
              id="inputDate"
              label={t('components.dateRangePicker.selectDate')}
              value={valueFrom}
              onChange={onChangeStartValue}
              error={getInputError(true)}
              setIsWrongDate={updateInvalidState}
            />
          ) : (
            <>
              <StyledDateTimeInput
                id="inputFrom"
                label={t('components.dateRangePicker.from')}
                value={valueFrom}
                onChange={onChangeStartValue}
                error={getInputError(true)}
                setIsWrongDate={updateInvalidState}
              />
              <StyledDateTimeInput
                id="inputTo"
                label={t('components.dateRangePicker.to')}
                value={valueTo}
                onChange={onChangeEndValue}
                error={getInputError(false)}
                setIsWrongDate={updateInvalidState}
              />
            </>
          )}
        </InputsContainer>
        {!singleDateSelection && !hidePredefinedSelections && (
          <PredefinedSelectionsRowContainer>
            <PredefinedSelections />
          </PredefinedSelectionsRowContainer>
        )}
        <StyledDateRangePicker
          numberOfCalendars={2}
          singleDateRange={singleDateSelection}
          value={valueRange}
          onSelect={handleSelect}
          onSelectStart={handleSelectStart}
          skipPendingState={shouldSkipPendingState()}
          maximumDate={maximumDate}
          minimumDate={minDate}
        />
      </RangeSelectionContainer>
      {!singleDateSelection && !hidePredefinedSelections && (
        <PredefinedSelectionsColumnContainer>
          <PredefinedSelections />
        </PredefinedSelectionsColumnContainer>
      )}
    </Container>
  );
};

export default DateRangePicker;
